(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 768px)';

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return '';
    try {
      const currency = window.Shopify?.currency?.active || 'CAD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const setHeaderHeight = () => {
    const header = document.querySelector('.swan-pro-header-wrapper')
      || document.querySelector('.site-header')
      || document.querySelector('header');

    const height = header
      ? Math.max(0, Math.round(header.getBoundingClientRect().height))
      : 116;

    document.documentElement.style.setProperty('--swan-commerce-header-height', `${height}px`);
    document.documentElement.style.setProperty('--swan-commerce-header-offset', `${height}px`);

    if (!window.matchMedia(MOBILE_QUERY).matches) {
      document.body.classList.remove('swan-mobile-buybar-active');
    }
  };

  const initCollectionVideos = (scope = document) => {
    scope.querySelectorAll('[data-swan-collection-hero]').forEach((hero) => {
      if (hero.dataset.swanVideoReady === 'true') return;
      hero.dataset.swanVideoReady = 'true';

      const video = hero.querySelector('video');
      const toggle = hero.querySelector('[data-swan-video-toggle]');
      if (!video) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let manuallyPaused = reducedMotion.matches;

      if (reducedMotion.matches) video.pause();

      const syncState = () => {
        if (!toggle) return;
        const paused = video.paused;
        toggle.classList.toggle('is-paused', paused);
        toggle.setAttribute('aria-label', paused ? 'Play collection video' : 'Pause collection video');
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      };

      if (toggle) {
        toggle.addEventListener('click', () => {
          if (video.paused) {
            manuallyPaused = false;
            video.play().catch(() => {});
          } else {
            manuallyPaused = true;
            video.pause();
          }
          syncState();
        });
      }

      video.addEventListener('play', syncState);
      video.addEventListener('pause', syncState);

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry.isIntersecting) {
            video.pause();
          } else if (!manuallyPaused && !reducedMotion.matches) {
            video.play().catch(() => {});
          }
        }, { threshold: 0.15 });
        observer.observe(hero);
      }

      syncState();
    });
  };

  const initMobileBuybars = (scope = document) => {
    scope.querySelectorAll('[data-swan-product-shell]').forEach((shell) => {
      if (shell.dataset.swanBuybarReady === 'true') return;
      shell.dataset.swanBuybarReady = 'true';

      const productId = shell.dataset.productId || '';
      const buybar = shell.querySelector('[data-swan-mobile-buybar]')
        || document.querySelector(`[data-swan-mobile-buybar][data-product-id="${CSS.escape(productId)}"]`);
      const form = shell.querySelector('form[action*="/cart/add"]')
        || shell.querySelector('product-form form');
      const originalButton = form?.querySelector('[data-add-to-cart], button[name="add"], button[type="submit"]');
      const barButton = buybar?.querySelector('[data-swan-mobile-add]');
      const barPrice = buybar?.querySelector('[data-swan-mobile-price]');
      const metaSku = shell.querySelector('[data-swan-product-sku]');
      const metaAvailability = shell.querySelector('[data-swan-product-availability]');

      if (!buybar || !form || !barButton || !originalButton) return;

      const syncButton = () => {
        const originalText = originalButton.querySelector('[data-add-to-cart-text]')?.textContent
          || originalButton.textContent
          || 'Add to cart';
        barButton.textContent = originalText.trim();
        barButton.disabled = Boolean(originalButton.disabled);
      };

      const syncPriceFromPage = () => {
        const price = shell.querySelector('[data-product-price]')
          || shell.querySelector('.product__price')
          || shell.querySelector('[data-product-price-list]')
          || shell.querySelector('price-list');
        if (price && barPrice) barPrice.textContent = price.textContent.trim();
      };

      const setVisible = (visible) => {
        buybar.classList.toggle('is-visible', visible);
        const active = visible && window.matchMedia(MOBILE_QUERY).matches;
        document.body.classList.toggle('swan-mobile-buybar-active', active);
      };

      barButton.addEventListener('click', () => {
        if (barButton.disabled) return;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(originalButton);
        } else {
          originalButton.click();
        }
      });

      const buttonObserver = new MutationObserver(() => {
        syncButton();
        syncPriceFromPage();
      });
      buttonObserver.observe(originalButton, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });

      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }, { threshold: 0.08 });
        visibilityObserver.observe(originalButton);
      }

      shell.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;

        if (barPrice) barPrice.textContent = formatMoney(variant.price);

        if (metaSku) {
          metaSku.textContent = variant.sku || '';
          metaSku.closest('.swan-product-sku')?.classList.toggle('hide', !variant.sku);
        }

        if (metaAvailability) {
          metaAvailability.textContent = variant.available ? 'Available to order' : 'Contact for availability';
          metaAvailability.classList.toggle('swan-product-kicker__status--unavailable', !variant.available);
        }

        window.setTimeout(() => {
          syncButton();
          syncPriceFromPage();
        }, 0);
      });

      /* Some versions of the parent theme dispatch the event on document. */
      document.addEventListener('variant:change', (event) => {
        if (shell.contains(event.target)) return;
        const variant = event.detail?.variant;
        if (!variant) return;
        if (barPrice) barPrice.textContent = formatMoney(variant.price);
        window.setTimeout(syncButton, 0);
      });

      syncButton();
      syncPriceFromPage();
    });
  };

  const addBusinessDays = (startDate, businessDays) => {
    const date = new Date(startDate.getTime());
    date.setHours(12, 0, 0, 0);
    let added = 0;

    while (added < businessDays) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }

    return date;
  };

  const initDeliveryDates = (scope = document) => {
    scope.querySelectorAll('[data-swan-order-summary]').forEach((summary) => {
      if (summary.dataset.swanDeliveryReady === 'true') return;
      summary.dataset.swanDeliveryReady = 'true';

      const output = summary.querySelector('[data-swan-delivery-date]');
      if (!output) return;

      const businessDays = Math.max(1, Number.parseInt(summary.dataset.deliveryDays || '2', 10) || 2);
      const deliveryDate = addBusinessDays(new Date(), businessDays);
      const locale = document.documentElement.lang || 'en-CA';

      try {
        output.textContent = new Intl.DateTimeFormat(locale, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }).format(deliveryDate);
      } catch (error) {
        output.textContent = deliveryDate.toLocaleDateString();
      }

      const isoDate = [
        deliveryDate.getFullYear(),
        String(deliveryDate.getMonth() + 1).padStart(2, '0'),
        String(deliveryDate.getDate()).padStart(2, '0'),
      ].join('-');
      output.setAttribute('datetime', isoDate);
    });
  };

  const enhanceProductTables = (root) => {
    root.querySelectorAll('table').forEach((table) => {
      if (table.dataset.swanTableReady === 'true') return;
      table.dataset.swanTableReady = 'true';

      const rows = Array.from(table.rows || []);
      if (!rows.length) return;

      const columnCount = rows.reduce((largest, row) => Math.max(largest, row.cells.length), 0);
      table.style.setProperty('width', '100%', 'important');
      table.style.removeProperty('min-width');
      rows.forEach((row) => Array.from(row.cells).forEach((cell) => {
        cell.style.removeProperty('width');
        cell.style.removeProperty('min-width');
        cell.style.removeProperty('max-width');
      }));
      const headerCells = Array.from(table.querySelectorAll('thead tr:first-child th'));
      const fallbackHeaders = headerCells.length
        ? headerCells
        : Array.from(rows[0]?.querySelectorAll('th') || []);
      const labels = fallbackHeaders.map((cell) => cell.textContent.trim());

      Array.from(table.tBodies || []).forEach((body) => {
        Array.from(body.rows).forEach((row) => {
          Array.from(row.cells).forEach((cell, index) => {
            if (labels[index]) cell.dataset.label = labels[index];
          });
        });
      });

      if (columnCount <= 2) table.classList.add('swan-spec-table--stacked');

      if (!table.parentElement?.classList.contains('swan-responsive-table')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'swan-responsive-table';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', 'Product specifications');
        wrapper.tabIndex = 0;
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    });
  };

  const calculatorSignal = (value = '') => {
    const text = value.replace(/\s+/g, ' ').trim().toLowerCase();
    return text.includes('calculator')
      || text.includes('your wall height')
      || text.includes('your wall width')
      || text.includes('total boxes you need')
      || text.includes('total boxes needed');
  };

  const markCalculatorFields = (calculator) => {
    calculator.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea').forEach((control) => {
      const field = control.closest('label, .field, .input-group, p, li, td, div');
      if (field && field !== calculator) field.classList.add('swan-calculator-field');
    });

    const resultCandidates = Array.from(calculator.querySelectorAll('p, li, label, div, output, strong'))
      .filter((element) => {
        const text = element.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
        return text.includes('total boxes you need') || text.includes('total boxes needed');
      })
      .sort((a, b) => a.textContent.length - b.textContent.length);

    if (resultCandidates.length) {
      let result = resultCandidates[0];
      const compactInlineResult = result.matches('span, strong, label, output');
      if (compactInlineResult && result.parentElement && result.parentElement !== calculator) {
        const parentText = result.parentElement.textContent.replace(/\s+/g, ' ').trim();
        if (parentText.length <= 180) result = result.parentElement;
      }
      if (result !== calculator) result.classList.add('swan-calculator-result');
    }
  };

  const enhanceProductCalculators = (root) => {
    const calculators = new Set();

    root.querySelectorAll('[class*="calculator" i], [id*="calculator" i]').forEach((candidate) => {
      if (candidate === root) return;
      if (candidate.matches('.swan-calculator-field, .swan-calculator-result, .swan-calculator-grid')) return;

      const calculatorAncestor = candidate.parentElement?.closest('[class*="calculator" i], [id*="calculator" i]');
      if (calculatorAncestor && calculatorAncestor !== root) return;

      const candidateText = candidate.textContent || '';
      const candidateControls = candidate.querySelectorAll('input:not([type="hidden"]), select, textarea').length;
      if (calculatorSignal(candidateText) || candidateControls >= 2) calculators.add(candidate);
    });

    const headings = Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6'))
      .filter((heading) => heading.textContent.toLowerCase().includes('calculator'));

    headings.forEach((heading) => {
      const existing = heading.closest('.swan-calculator-block, [class*="calculator" i], [id*="calculator" i]');
      if (existing && existing !== root) {
        calculators.add(existing);
        return;
      }

      const parent = heading.parentElement;
      if (!parent) return;

      const parentText = parent.textContent || '';
      const parentHasControls = Boolean(parent.querySelector('input, select, textarea'));
      const precedingContent = Array.from(parent.children)
        .slice(0, Math.max(0, Array.from(parent.children).indexOf(heading)))
        .some((element) => !['SCRIPT', 'STYLE'].includes(element.tagName) && element.textContent.trim());
      if (parent !== root && parentHasControls && calculatorSignal(parentText) && !precedingContent) {
        calculators.add(parent);
        return;
      }

      const headingLevel = Number.parseInt(heading.tagName.slice(1), 10) || 6;
      const nodes = [];
      let cursor = heading;
      let safety = 0;

      while (cursor && safety < 30) {
        if (cursor !== heading && /^H[1-6]$/.test(cursor.tagName)) {
          const level = Number.parseInt(cursor.tagName.slice(1), 10) || 6;
          if (level <= headingLevel) break;
        }
        nodes.push(cursor);
        cursor = cursor.nextElementSibling;
        safety += 1;
      }

      const combinedText = nodes.map((node) => node.textContent || '').join(' ');
      const hasControls = nodes.some((node) => node.matches?.('input, select, textarea') || node.querySelector?.('input, select, textarea'));
      if (!hasControls && !calculatorSignal(combinedText)) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'swan-calculator-block';
      heading.parentNode.insertBefore(wrapper, heading);
      nodes.forEach((node) => wrapper.appendChild(node));
      calculators.add(wrapper);
    });

    if (!calculators.size) {
      const rootText = (root.textContent || '').replace(/\s+/g, ' ').toLowerCase();
      const controls = root.querySelectorAll('input:not([type="hidden"]), select, textarea');
      const hasMeasurementSignals = rootText.includes('your wall height')
        && rootText.includes('your wall width')
        && (rootText.includes('total boxes') || rootText.includes('calculator'));
      if (controls.length >= 2 && hasMeasurementSignals) calculators.add(root);
    }

    calculators.forEach((calculator) => {
      calculator.classList.add('swan-calculator-block', 'swan-calculator-legacy');
      markCalculatorFields(calculator);

      const controls = Array.from(calculator.querySelectorAll(
        'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea'
      ));
      const fields = Array.from(new Set(controls.map((control) => control.closest('.swan-calculator-field')).filter(Boolean)));
      if (fields.length >= 2) {
        const parent = fields[0].parentElement;
        const sameParent = parent && fields.every((field) => field.parentElement === parent);
        if (sameParent && parent !== calculator && !parent.closest('table')) {
          parent.classList.add('swan-calculator-grid');
        } else if (sameParent && parent === calculator) {
          calculator.classList.add('swan-calculator-grid');
        }
      }
    });
  };

  const initProductDetails = (scope = document) => {
    scope.querySelectorAll('.swan-product-details-card').forEach((details) => {
      enhanceProductTables(details);
      enhanceProductCalculators(details);
      details.dataset.swanDetailsReady = 'true';
    });
  };

  const initProductCardEqualizers = (scope = document) => {
    scope.querySelectorAll('[data-swan-product-shell]').forEach((shell) => {
      if (shell.dataset.swanEqualizerReady === 'true') return;

      const mediaCard = shell.querySelector('.swan-product-media-card');
      const mediaRoot = mediaCard?.querySelector('[data-product-images]');
      const detailsCard = shell.querySelector('.swan-product-card.product-single__meta');
      if (!mediaCard || !mediaRoot || !detailsCard) return;

      shell.dataset.swanEqualizerReady = 'true';
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const isSlider = mediaRoot.dataset.hasSlideshow === 'true';
      let frame = 0;

      const sync = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          if (!desktopQuery.matches || !isSlider) {
            mediaCard.classList.remove('is-swan-equalized');
            mediaCard.style.removeProperty('--swan-product-equal-height');
            return;
          }

          const measuredHeight = Math.ceil(detailsCard.getBoundingClientRect().height);
          if (measuredHeight > 0) {
            mediaCard.classList.add('is-swan-equalized');
            mediaCard.style.setProperty('--swan-product-equal-height', `${measuredHeight}px`);
          }
        });
      };

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(sync);
        observer.observe(detailsCard);
      }

      mediaCard.querySelectorAll('img, video').forEach((media) => {
        if (media.complete || media.readyState >= 2) return;
        media.addEventListener('load', sync, { once: true });
        media.addEventListener('loadedmetadata', sync, { once: true });
      });

      if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', sync);
      } else if (typeof desktopQuery.addListener === 'function') {
        desktopQuery.addListener(sync);
      }

      shell.addEventListener('variant:change', () => window.setTimeout(sync, 0));
      window.addEventListener('load', sync, { once: true });
      sync();
    });
  };



  const initProductViewportFit = (scope = document) => {
    scope.querySelectorAll('[data-swan-product-shell]').forEach((shell) => {
      if (shell.dataset.swanViewportFitReady === 'true') return;

      const detailsCard = shell.querySelector('.swan-product-card.product-single__meta')
        || shell.querySelector('sticky-scroller > product-form');
      if (!detailsCard) return;

      shell.dataset.swanViewportFitReady = 'true';
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      let frame = 0;

      const measure = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          shell.classList.remove('swan-pdp-density--compact', 'swan-pdp-density--tight');

          if (!desktopQuery.matches) {
            shell.dataset.swanViewportFit = 'mobile';
            return;
          }

          const rootStyles = getComputedStyle(document.documentElement);
          const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--swan-commerce-header-offset'))
            || Number.parseFloat(rootStyles.getPropertyValue('--swan-commerce-header-height'))
            || 116;
          const cardTop = Math.max(headerHeight + 8, detailsCard.getBoundingClientRect().top);
          const available = Math.max(480, window.innerHeight - cardTop - 10);

          let mode = 'comfortable';
          if (detailsCard.getBoundingClientRect().height > available + 2) {
            shell.classList.add('swan-pdp-density--compact');
            mode = 'compact';
          }

          if (detailsCard.getBoundingClientRect().height > available + 2) {
            shell.classList.add('swan-pdp-density--tight');
            mode = 'tight';
          }

          shell.dataset.swanViewportFit = mode;
        });
      };

      if ('MutationObserver' in window) {
        const observer = new MutationObserver(measure);
        observer.observe(detailsCard, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'aria-hidden', 'data-rating', 'data-raters']
        });
      }

      if (typeof desktopQuery.addEventListener === 'function') desktopQuery.addEventListener('change', measure);
      else if (typeof desktopQuery.addListener === 'function') desktopQuery.addListener(measure);

      shell.addEventListener('variant:change', () => window.setTimeout(measure, 0));
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure, { once: true });
      measure();
    });
  };

  const initProductExperienceSuites = (scope = document) => {
    scope.querySelectorAll('[data-swan-pdp-suite]').forEach((suite) => {
      if (suite.dataset.swanSuiteReady === 'true') return;
      suite.dataset.swanSuiteReady = 'true';

      suite.querySelectorAll('[data-swan-before-after]').forEach((comparison) => {
        const range = comparison.querySelector('[data-swan-before-after-range]');
        if (!range) return;
        const update = () => comparison.style.setProperty('--swan-compare-position', `${range.value}%`);
        range.addEventListener('input', update, { passive: true });
        update();
      });

      suite.querySelectorAll('[data-swan-rail-prev], [data-swan-rail-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const targetId = button.getAttribute('aria-controls');
          const rail = targetId ? document.getElementById(targetId) : null;
          if (!rail) return;
          const direction = button.hasAttribute('data-swan-rail-prev') ? -1 : 1;
          rail.scrollBy({ left: direction * Math.max(260, rail.clientWidth * 0.82), behavior: 'smooth' });
        });
      });

      suite.querySelectorAll('.swan-pdp-faq__item').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          suite.querySelectorAll('.swan-pdp-faq__item[open]').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });

      suite.querySelectorAll('form.swan-addon-card__form').forEach((form) => {
        if (form.dataset.swanAjaxReady === 'true') return;
        form.dataset.swanAjaxReady = 'true';
        const button = form.querySelector('.swan-addon-card__button');
        if (window.theme?.AjaxProduct && button) {
          new window.theme.AjaxProduct(form, '.swan-addon-card__button');
        }
      });
    });
  };

  const init = (scope = document) => {
    setHeaderHeight();
    initCollectionVideos(scope);
    initMobileBuybars(scope);
    initDeliveryDates(scope);
    initProductDetails(scope);
    initProductCardEqualizers(scope);
    initProductViewportFit(scope);
    initProductExperienceSuites(scope);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('load', setHeaderHeight, { once: true });
  window.addEventListener('resize', () => window.requestAnimationFrame(setHeaderHeight), { passive: true });
})();
